import React, { useContext, useState } from "react"
import Wave, { WaveType } from "../ui/wave"
import "firebase/firestore"
import firebase from "gatsby-plugin-firebase"
import { Button } from "@cruto/cruto-core"
import {
  Box,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  CircularProgress,
  Snackbar,
} from "@material-ui/core"

import Referrer from "./Referrer"
import { getClientTranslations } from "../layout/helperFunctions"
import AppContext from "../../common/appContext"

type Props = {
  heading: string
  body: {
    html: string
  }
}

const TopSection = ({ heading, body }: Props) => {
  const [email, setEmail] = useState<string | null>(null)
  const [personalLink, setPersonalLink] = useState<string | null>(null)
  const { locale } = useContext(AppContext)
  const [submitting, setSubmitting] = useState<boolean | null>(false)
  const [snackbarOpen, setSnackbarOpen] = useState<boolean | null>(false)
  const [emailError, setEmailError] = useState<string | null>(null)

  const pattern = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

  const createNewReferrer = async (
    collection: firebase.firestore.CollectionReference<firebase.firestore.DocumentData>
  ): Promise<string> => {
    const newDocReference = collection.doc()
    const referrer: Referrer = { email: email }
    await newDocReference.set(referrer)
    return newDocReference.id
  }

  const validateForm = (): boolean => {
    if (!email) {
      setEmailError(getClientTranslations("Email is required", locale))
      return false
    } else if (!pattern.test(email)) {
      setEmailError(getClientTranslations("Email has wrong format", locale))
      return false
    } else return true
  }

  const shareLink = async (link: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Cruto techAgent",
          url: link,
        })
      } catch {}
    } else {
      await navigator.clipboard.writeText(link)
      setSnackbarOpen(true)
      setTimeout(() => setSnackbarOpen(false), 3000)
    }
  }

  const generatePersonalLink = async (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    evt.preventDefault()
    if (!validateForm()) {
      return
    }
    setSubmitting(true)
    const referrerCollection = firebase?.firestore().collection("referrer")
    const existingReferrer = await referrerCollection
      ?.where("email", "==", email)
      .limit(1)
      .get()
    const referrerId =
      existingReferrer?.docs[0]?.id ??
      (await createNewReferrer(referrerCollection))

    const resp = await fetch(
      `${process.env.GATSBY_GENERATE_PERSONAL_AD_LINK_URL}/?referrerId=${referrerId}&adId=${process.env.GATSBY_CRUTO_CONSULTING_AD_ID}`
    )
    const json = await resp.json()

    setPersonalLink(json.shortLink)
    setSubmitting(false)
    await shareLink(json.shortLink)
  }

  return (
    <div className="u-bg-green pt-5">
      {submitting && <div className="overlay"></div>}
      <section className="min-vh-50 d-flex align-items-center">
        <div className={"container"}>
          <div className="row">
            <div className="align-items-center col-12 col-md-6 d-flex order-last order-md-0 p-3">
              <div
                data-sal="slide-up"
                data-sal-delay="200"
                data-sal-easing="ease"
                className="text-center text-md-start pt-4 pt-md-0"
              >
                <h2 className={"u-c-white"}>{heading}</h2>
                <div
                  className="u-lead-md pt-3 u-c-white u-o-1"
                  dangerouslySetInnerHTML={{ __html: body.html }}
                />
              </div>
            </div>
          </div>
          <div className={"row u-mx-0 u-my-0"}>
            {!personalLink && !submitting && (
              <Box component={"form"} style={{ padding: "8px" }}>
                <FormControl
                  error={emailError != null}
                  required
                  className="col-sm-3 col-12"
                >
                  <>
                    <InputLabel htmlFor="component-outlined">
                      {getClientTranslations("Your email", locale)}
                    </InputLabel>
                    <Input
                      style={{ marginBottom: "8px" }}
                      id="component-outlined"
                      type="email"
                      onChange={evt => {
                        setEmail(evt.target.value.trim())
                        if (emailError) setEmailError(null)
                      }}
                    />
                    {emailError && (
                      <FormHelperText>{emailError}</FormHelperText>
                    )}
                    <Button
                      className={"u-bg-orange u-c-white py-2"}
                      variant="contained"
                      type="submit"
                      onClick={async evt => await generatePersonalLink(evt)}
                    >
                      {getClientTranslations("Generate link", locale)}
                    </Button>
                  </>
                </FormControl>
              </Box>
            )}
            {personalLink && (
              <div className={"row u-mx-0 u-my-0"}>
                <div className={"col-sm-3 col-12"}>
                  <Button
                    className={"u-bg-orange u-c-white p-2"}
                    variant="contained"
                    onClick={async () => await shareLink(personalLink)}
                  >
                    {getClientTranslations("Copy personal link", locale)}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        {submitting && (
          <CircularProgress
            style={{
              position: "absolute",
              top: "50%",
              bottom: 0,
              left: 0,
              right: 0,
              marginLeft: "auto",
              marginRight: "auto",
              marginTop: "auto",
              marginBottom: "auto",
              width: "100px",
              height: "100px",
            }}
          />
        )}
      </section>

      <Wave className={"u-c-white"} type={WaveType.v2} />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={getClientTranslations(
          "Personal link copied to clipboard",
          locale
        )}
        key={"personal-link-copied"}
      />
    </div>
  )
}

export default TopSection
